import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import useDebounce from '../../../../components/molecules/Form/components/ComboBox/AbstractComboBox/hooks/useDebounce';

type InputCounterProps = {
  maxLength: number;
  elementRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
};

export function InputCounter({ elementRef, maxLength }: InputCounterProps) {
  const { t } = useSonTranslation('InputCounter');
  const [currentCount, setCurrentCount] = useState<number>(0);
  const debouncedCurrentCount = useDebounce<number>(currentCount, 300);
  const [message, setMessage] = useState<string>('');

  /**
   * Calculates the remaining chars for screen reader output. Generates the message sent to screen readers.
   * This uses the debounce value so we message will be calculated when user stops typing
   */
  const getScreenReaderOutput = useCallback((): string => {
    const remainingChars = maxLength - debouncedCurrentCount.value;
    return remainingChars > 0
      ? t('screenReaderMessageWarning', { remainingChars })
      : t('screenReaderMessageError', { currentCount: debouncedCurrentCount.value });
  }, [debouncedCurrentCount, maxLength, t]);

  useEffect(() => {
    setMessage(getScreenReaderOutput());
  }, [getScreenReaderOutput]);

  useEffect(() => {
    const inputElement = elementRef.current;
    // initialize input length with this here
    setCurrentCount(elementRef.current?.value?.length || 0);
    const setCountFromRef = (event: Event) => {
      const target = event.target as HTMLInputElement;
      setCurrentCount((target.value?.length as number) || 0);
    };

    inputElement?.addEventListener('keyup', setCountFromRef);
    return () => {
      inputElement?.removeEventListener('keyup', setCountFromRef);
    };
  }, [elementRef]);

  /**
   * 10% of maxLength characters before maxLength we change the counter look to orange (warning).
   * When we reach maxlength, it's set to red (error).
   * We additionally add an invisible border for people using WIN HCM
   */
  const changeCounterLook = () => {
    const percentage = Math.round((10 / 100) * maxLength);
    const basicClasses = 'form2-input-counter__count font-regular-11-11';

    if (currentCount >= maxLength - percentage && currentCount <= maxLength) {
      return basicClasses + ' form2-input-counter__count--warning';
    }
    if (currentCount > maxLength) {
      return basicClasses + ' form2-input-counter__count--error';
    }
    return basicClasses;
  };

  /**
   * Counter consists of two elements: the counter and a corresponding live region.
   * Live region is display:none until corresponding input field is focused (see CSS).
   * Counter is aria-hidden="true" to prevent reading via screen reader focus.
   */
  return (
    <span className="form2-input-counter">
      <span className={changeCounterLook()} aria-hidden="true">
        {`${currentCount} / ${maxLength}`}
      </span>
      <span aria-live="polite" aria-atomic="true" className="form2-input-counter__live-region">
        {message}
      </span>
    </span>
  );
}
